import { Box, Card, CardBody, Divider, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import React from 'react';

import { getUTCDate } from '../../../../../utils/dates';
import { formatMoney } from '../../../../../utils/helpers';
import { DealStateEnum } from '../../../../../utils/types/deal';
import { DealStateMap, DealWithFarthestState } from '../../../utils';
import { formatBank } from '../utils';

type Props = { deal: DealWithFarthestState };

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box flex={{ md: 1 }}>
    <Flex
      direction={{ base: 'row', md: 'column' }}
      justify={{ base: 'space-between', md: 'flex-start' }}
      align={{ base: 'center', md: 'flex-start' }}
      gap={{ base: 0, md: 1 }}
    >
      <Text fontSize="xs" fontWeight="700" color="gray.500" letterSpacing="wider">
        {label}
      </Text>
      <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="800" color="leaseEndBlue">
        {value}
      </Text>
    </Flex>
  </Box>
);

const VehicleSummaryCard = ({ deal }: Props) => {
  const car = deal?.car;
  const financial = deal?.financial_info;
  const showFinancing =
    DealStateMap[deal?.farthestState as DealStateEnum] >= DealStateMap[DealStateEnum.Funded];

  const vehicleName = [car?.year, car?.make, car?.model].filter(Boolean).join(' ');
  const firstPayment = financial?.first_payment_date
    ? getUTCDate(financial.first_payment_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'TBD';

  return (
    <Card borderRadius="24px" p={2}>
      <CardBody p={4} bgColor="grayCoolLight" borderRadius="16px">
        <Box>
          <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800" color="leaseEndBlue">
            {vehicleName}
          </Text>
          {car?.vin && (
            <HStack spacing={2} mt={1}>
              <Text fontSize="xs" color="gray.500" fontWeight="700" letterSpacing="wider">
                VIN
              </Text>
              <Text fontFamily="mono" fontSize="xs" color="gray.600" letterSpacing="0.05em">
                {car.vin}
              </Text>
            </HStack>
          )}
        </Box>
        {showFinancing && (
          <>
            <Divider my={4} borderColor="gray.300" />
            <Text fontSize="xs" fontWeight="700" color="gray.500" letterSpacing="wider" mb={1}>
              YOUR MONTHLY PAYMENT
            </Text>
            <Text fontSize="4xl" fontWeight="800" color="leaseEndBlue" lineHeight="1.1">
              {formatMoney(financial?.payment ?? 0)}
            </Text>
            <Stack
              direction={{ base: 'column', md: 'row' }}
              divider={
                <Box
                  borderLeftWidth={{ base: 0, md: '1px' }}
                  borderTopWidth={{ base: '1px', md: 0 }}
                  borderColor="gray.300"
                />
              }
              spacing={{ base: 2, md: 4 }}
              mt={3}
            >
              <Stat label="APR" value={`${financial?.sell_rate ?? 0}%`} />
              <Stat label="Loan Term" value={`${financial?.term ?? 0} mos`} />
              <Stat label="First Payment" value={firstPayment} />
            </Stack>
            <Text fontSize="sm" color="gray.600" mt={4}>
              Financed through <b>{formatBank(deal)}</b>
            </Text>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default VehicleSummaryCard;
